import { InfotoolAPI, LogEntry, ReportEntry, SystemEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { debounce, getBrowserDetails, getViewportDimensions } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { triggerEvent } from '../../../loader/essentials/events.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { logEntries$ } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ADSLOT_REGISTRY, METATAG } from '../../../loader/token.js';
import { toAdSizesString } from '../../../utils/adsize-helper.js';
import { getIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { Adslot } from '../adslot-controller/adslot.js';

@injectionTarget()
export class PublicInfotoolAPI implements InfotoolAPI {

    @inject(METATAG) private metaTag!: MetaTag;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;

    public readonly reportEntries: ReportEntry[] = [];
    public readonly logEntries: LogEntry[] = [];

    constructor() {
        this.metaTag.Infotool = this;

        this.#addListeners();

        // provide legacy logs for infotool for now - currently the logEntries array is fetched once and the SDG_NEW_LOG_ENTRY event notifies about additional entries one by one
        logEntries$.subscribe(logEntry => {
            this.logEntries.push(logEntry);
            triggerEvent(SystemEvent.SDG_NEW_LOG_ENTRY, { logEntry });
        });
    }

    public get placementCount(): number {
        return this.adslotRegistry.getAdslots().length;
    }

    #addListeners(win: Window = window) {
        /** helper function to add reportEntry with default values and current date */
        const addEntry = (reportEntry: Partial<ReportEntry>) => this.reportEntries.push({ slot: 'system_msg', message: '', date: new Date(), ...reportEntry });

        const { browserName, browserVersion, os, isPhone, isTablet } = getBrowserDetails();
        addEntry({ message: `Time of page request: ${(new Date().toUTCString())}` });
        addEntry({ message: `Browser used: ${browserName} version: ${browserVersion}` });
        addEntry({ message: `Operating system: ${os}, mobile device: ${(isPhone || isTablet) ? 'yes' : 'no'}` });

        const reportViewport = () => {
            const { viewportWidth, viewportHeight } = getViewportDimensions();
            addEntry({ message: `Viewport: ${viewportWidth}x${viewportHeight}` });
        };
        reportViewport();
        win.addEventListener('resize', _ => debounce(reportViewport, 50)); // listen for window resize

        const reportUrl = () => {
            addEntry({ message: `Full URL: ${document.location.href}` });
        };
        reportUrl();
        win.addEventListener('popstate', _ => debounce(reportUrl, 10)); // report changed url for single page applications

        win.addEventListener(SystemSlotEvent.SDG_SLOT_REGISTERED, ({ detail }) => {
            addEntry({ slot: detail.slot, message: 'registered.' });
        });
        win.addEventListener(SystemSlotEvent.SDG_SLOT_DELETED, ({ detail }) => {
            addEntry({ slot: detail.slot, message: 'deleted.' });
        });
        win.addEventListener(SystemSlotEvent.SDG_SLOT_CALLING, ({ detail }) => {
            const adSlot = (detail.placement as Adslot);
            addEntry({ slot: detail.slot, message: `calling ${adSlot.options.lazyLoad ? 'lazy-load' : 'non-lazy'} slot. Zone: ${adSlot.getZone()}, AdUnit: ${constructAdUnitPath(adSlot)}, Sizes: ${toAdSizesString(adSlot.getSizes(), ', ')}` });
        });
        win.addEventListener(SystemSlotEvent.SDG_SLOT_DONE, ({ detail }) => {
            const adSlot = (detail.placement as Adslot);
            addEntry({ slot: detail.slot, message: `${adSlot.options.lazyLoad ? 'lazy-load' : 'non-lazy'} slot finished. ID dump: ${JSON.stringify(getIdsForAdSlot(adSlot.slotName))}` });
        });
        win.addEventListener(SystemSlotEvent.SDG_SLOT_WILL_RELOAD, ({ detail }) => {
            const adSlot = (detail.placement as Adslot);
            addEntry({ slot: detail.slot, message: `reports reload. Zone: ${adSlot.getZone()}, AdUnit: ${constructAdUnitPath(adSlot)}, Sizes: ${toAdSizesString(adSlot.getSizes(), ', ')}` });
        });
        win.addEventListener(SystemSlotEvent.SDG_SLOT_YL_RENDER_ERROR, ({ detail }) => {
            addEntry({ slot: detail.slot, message: `reports a fatal render error for creative from HeaderBidder. ID dump: ${JSON.stringify(getIdsForAdSlot(detail.slot))}` });
        });
    }
}
